import {
  AutocompleteInput,
  DateInput,
  Modal,
  NumberInput,
  SelectInput,
  SwitchInput,
  TextField,
  Typography,
} from 'melp-design/components';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { useQueryClient } from '@tanstack/react-query';
import { Stack, Tooltip } from '@mui/material';
import { InfoOutlined } from '@mui/icons-material';
import { ModalProps } from 'store/modal';
import { ShopItemCategory, shopItemCategories } from 'types/MarketplaceItems';
import { usePredefinedToasts } from 'utils/Toast';
import {
  ExpenseStatus,
  expenseStatuses,
  useUpdateExpense,
} from 'store/expenses';
import { employeesKeys } from 'store/employees';
import { Controller, SubmitHandler, useForm } from 'react-hook-form-latest';
import { useBenefits, useEmployeeBenefits } from 'store/benefits';

interface Values {
  date: string;
  status: ExpenseStatus;
  benefits: string[];
  useRecognition: boolean;
  categories: ShopItemCategory[];
  amount: number;
  description: string;
}

interface Props extends ModalProps {
  expenseId: string;
  employeeId: string;
  values: Values;
}

export const EditExpenseModal: FC<Props> = ({
  expenseId,
  employeeId,
  values,
  closeModal,
}) => {
  const { t } = useTranslation();
  const predefinedToasts = usePredefinedToasts();
  const queryClient = useQueryClient();

  const { control, handleSubmit, watch } = useForm<Values>({
    defaultValues: values,
  });

  const { data: benefitOptions, isInitialLoading: isBenefitsOptionsLoading } =
    useEmployeeBenefits(employeeId, { trackBalance: true });
  const {
    data: preselectedBenefitOptions,
    isInitialLoading: isPreselectedBenefitsOptionsLoading,
  } = useBenefits(
    { benefits: values.benefits },
    { enabled: !!values.benefits.length },
  );

  const { mutate: updateExpense, isLoading } = useUpdateExpense();

  const onSubmit: SubmitHandler<Values> = ({
    date,
    status,
    benefits,
    useRecognition,
    categories,
    amount,
    description,
  }) => {
    updateExpense(
      {
        expenseId,
        status,
        date,
        amount,
        benefits,
        recognition: useRecognition,
        shopCategories: categories,
        description,
      },
      {
        onSuccess: () => {
          queryClient.invalidateQueries(employeesKeys.detail(employeeId));
          predefinedToasts.success.added();
          closeModal({ action: 'CLOSE' });
        },
      },
    );
  };

  const [benefits, useRecognition] = watch(['benefits', 'useRecognition']);

  return (
    <form onSubmit={handleSubmit(onSubmit)}>
      <Modal.Content
        title={t('actions.on_specific_date')}
        actions={[
          {
            variant: 'neutral-outline',
            onClick: () => closeModal({ action: 'CLOSE' }),
            label: t('common.cancel'),
          },
          {
            variant: 'primary',
            label: t('common.confirm'),
            type: 'submit',
            disabled: isLoading,
          },
        ]}
      >
        <Stack gap="20px">
          <Controller
            name="status"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => {
              return (
                <SelectInput
                  label={t('common.status')}
                  name={name}
                  value={value}
                  onChange={onChange}
                  options={expenseStatuses
                    .filter((status) => ['completed', 'paid'].includes(status))
                    .map((status) => ({
                      label: t(`expenses.status.${status}`),
                      value: status,
                    }))}
                  required
                  ref={ref}
                />
              );
            }}
          />

          <Controller
            name="date"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <DateInput
                label={t('common.date')}
                name={name}
                value={value}
                onChange={onChange}
                required
                ref={ref}
              />
            )}
          />

          <Controller
            name="benefits"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => {
              return (
                <Stack gap={1}>
                  <Typography variant="p1" color="textSecondary">
                    <Stack flexDirection="row" gap={1} alignItems="center">
                      {t('wallet.employeeExpenseFundsSourceSectionTitle')}
                      <Tooltip
                        title={t(
                          'wallet.employeeExpenseFundsSourceSectionHint',
                        )}
                      >
                        <InfoOutlined fontSize="small" />
                      </Tooltip>
                    </Stack>
                  </Typography>

                  <AutocompleteInput
                    label={t('wallet.employeeExpenseFundsSource')}
                    name={name}
                    value={value}
                    multiple
                    disabled={
                      isBenefitsOptionsLoading ||
                      isPreselectedBenefitsOptionsLoading
                    }
                    required={!useRecognition}
                    onChange={onChange}
                    options={
                      benefitOptions?.map((benefit) => ({
                        label: benefit.name,
                        value: benefit.id,
                      })) ?? []
                    }
                    preselectedOptions={preselectedBenefitOptions?.items.map(
                      (benefit) => ({
                        value: benefit.id,
                        label: benefit.name,
                      }),
                    )}
                    ref={ref}
                  />
                </Stack>
              );
            }}
          />

          <Controller
            name="useRecognition"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => {
              return (
                <SwitchInput
                  label={t('wallet.employeeExpenseSourceRecognition')}
                  name={name}
                  value={value}
                  onChange={onChange}
                  required={!benefits.length}
                  ref={ref}
                />
              );
            }}
          />

          <Controller
            name="categories"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => {
              return (
                <AutocompleteInput
                  label={t('wallet.employeeExpenseItemCtegory')}
                  name={name}
                  value={value}
                  onChange={onChange}
                  options={shopItemCategories.map((category) => ({
                    label: t(`marketplaceItems.itemCategory-${category}`),
                    value: category,
                  }))}
                  multiple
                  required
                  ref={ref}
                />
              );
            }}
          />

          <Controller
            name="amount"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => (
              <NumberInput
                label={t('benefitExpenses.expenseAmount')}
                name={name}
                value={value}
                onChange={onChange}
                required
                key={value}
                ref={ref}
              />
            )}
          />

          <Controller
            name="description"
            control={control}
            render={({ field: { name, value, onChange, ref } }) => {
              return (
                <TextField
                  label={t('benefitExpenses.expenseDescription')}
                  name={name}
                  value={value}
                  onChange={onChange}
                  required
                  ref={ref}
                />
              );
            }}
          />
        </Stack>
      </Modal.Content>
    </form>
  );
};
